import React from 'react';
import { Text, Link, TextProps } from '@chakra-ui/react';

interface FormattedTextProps extends TextProps {
  children: string;
}

// Regex to match URLs (capturing group so split includes matches)
const URL_REGEX = /(https?:\/\/[^\s]+)/g;

// Punctuation that commonly appears after URLs in sentences
const TRAILING_PUNCT_REGEX = /[.,;:!]+$/;

const isUrl = (text: string) => text.startsWith('http://') || text.startsWith('https://');

// Clean trailing punctuation from URLs
const cleanUrl = (url: string): { url: string; trailing: string } => {
  const match = url.match(TRAILING_PUNCT_REGEX);
  if (match) {
    return {
      url: url.slice(0, -match[0].length),
      trailing: match[0],
    };
  }
  return { url, trailing: '' };
};

export const FormattedText: React.FC<FormattedTextProps> = ({ children, ...textProps }) => {
  if (!children) return null;

  // Split text by URLs - capturing group includes URLs in result array
  const parts = children.split(URL_REGEX);

  return (
    <Text whiteSpace="pre-wrap" {...textProps}>
      {parts.map((part, index) => {
        if (!part) return null;
        if (isUrl(part)) {
          const { url, trailing } = cleanUrl(part);
          return (
            <React.Fragment key={index}>
              <Link
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                color="brandColorScheme.300"
                textDecoration="underline"
              >
                {url}
              </Link>
              {trailing}
            </React.Fragment>
          );
        }
        return <React.Fragment key={index}>{part}</React.Fragment>;
      })}
    </Text>
  );
};
